import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getAllCoursesUnified } from '@/utils/allCourses';
import { toast } from 'sonner';

interface MigrationState {
  isRunning: boolean;
  progress: number;
  total: number;
  completed: number;
  errors: string[];
  hasRun: boolean;
  lastCheck: string | null;
}

interface MigrationResult {
  success: boolean;
  successCount: number;
  errorCount: number;
  errors: string[];
}

const EXPECTED_COURSE_COUNT = 120;
const MIGRATION_CHECK_KEY = 'course_migration_last_check';
const MIGRATION_VERSION_KEY = 'course_migration_version';
const CURRENT_VERSION = '1.0.0';

export const useAutoMigration = () => {
  const [state, setState] = useState<MigrationState>({
    isRunning: false,
    progress: 0,
    total: 0,
    completed: 0,
    errors: [],
    hasRun: false,
    lastCheck: localStorage.getItem(MIGRATION_CHECK_KEY)
  });

  const hasChecked = useRef(false);

  const checkMigrationNeeded = useCallback(async (): Promise<boolean> => {
    try {
      // Check if we've already run migration in this session
      const lastVersion = localStorage.getItem(MIGRATION_VERSION_KEY);
      const lastCheck = localStorage.getItem(MIGRATION_CHECK_KEY);
      const now = new Date().toISOString();
      
      // If we've checked recently (within 1 hour) and versions match, skip
      if (lastCheck && lastVersion === CURRENT_VERSION) {
        const checkTime = new Date(lastCheck);
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        if (checkTime > oneHourAgo) {
          return false;
        }
      }

      // Get current database course count
      const { count, error } = await supabase
        .from('courses')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      if (error) {
        console.warn('Failed to check course count:', error.message);
        return false;
      }

      const dbCourseCount = count || 0;
      const staticCourseCount = getAllCoursesUnified().length;

      console.log(`📊 Migration check: DB=${dbCourseCount}, Static=${staticCourseCount}, Expected=${EXPECTED_COURSE_COUNT}`);

      // Update last check time
      localStorage.setItem(MIGRATION_CHECK_KEY, now);

      // More aggressive migration threshold - trigger if missing more than 10 courses
      const missingCourses = staticCourseCount - dbCourseCount;
      const migrationNeeded = missingCourses > 10 || dbCourseCount < EXPECTED_COURSE_COUNT * 0.7;
      
      if (migrationNeeded) {
        console.log(`🔄 Auto-migration needed: ${missingCourses} courses missing (DB=${dbCourseCount}, Static=${staticCourseCount})`);
      }

      return migrationNeeded;
    } catch (error) {
      console.error('Migration check failed:', error);
      return false;
    }
  }, []);

  const runAutoMigration = useCallback(async (): Promise<boolean> => {
    if (state.isRunning) {
      return false;
    }

    try {
      setState(prev => ({ 
        ...prev, 
        isRunning: true, 
        progress: 0, 
        errors: [],
        total: getAllCoursesUnified().length
      }));

      console.log('🚀 Starting automated course migration...');
      toast.info('Syncing courses to database...', { duration: 2000 });

      const { data, error } = await supabase.functions.invoke('migrate-courses', {
        body: { courses: getAllCoursesUnified() }
      });

      if (error) {
        throw new Error(`Migration function error: ${error.message}`);
      }

      const result = data as MigrationResult;

      if (result.success) {
        console.log('✅ Auto-migration completed successfully');
        
        setState(prev => ({ 
          ...prev, 
          isRunning: false, 
          progress: 100,
          completed: result.successCount,
          hasRun: true,
          errors: result.errors || []
        }));

        // Mark migration as completed for this version
        localStorage.setItem(MIGRATION_VERSION_KEY, CURRENT_VERSION);
        localStorage.setItem(MIGRATION_CHECK_KEY, new Date().toISOString());

        if (result.successCount > 0) {
          toast.success(`Synced ${result.successCount} courses to database`, { duration: 3000 });
        }

        return true;
      } else {
        throw new Error('Migration failed');
      }
    } catch (error) {
      console.error('Auto-migration failed:', error);
      
      setState(prev => ({ 
        ...prev, 
        isRunning: false,
        errors: [...prev.errors, error instanceof Error ? error.message : 'Unknown error']
      }));

      // Don't show error toast for auto-migration failures to avoid disrupting UX
      console.warn('Auto-migration failed, will continue with static fallback');
      return false;
    }
  }, [state.isRunning]);

  const triggerMigrationIfNeeded = useCallback(async () => {
    if (hasChecked.current || state.hasRun) {
      return;
    }

    hasChecked.current = true;

    try {
      const needsMigration = await checkMigrationNeeded();
      if (needsMigration) {
        await runAutoMigration();
      }
    } catch (error) {
      console.warn('Migration check/trigger failed:', error);
    }
  }, [checkMigrationNeeded, runAutoMigration, state.hasRun]);

  // Auto-trigger migration check on component mount (more delayed to avoid blocking dashboard)
  useEffect(() => {
    // Delay more to avoid blocking dashboard load
    const timer = setTimeout(triggerMigrationIfNeeded, 3000);
    return () => clearTimeout(timer);
  }, [triggerMigrationIfNeeded]);

  const manualMigration = useCallback(async () => {
    return await runAutoMigration();
  }, [runAutoMigration]);

  const resetMigrationState = useCallback(() => {
    localStorage.removeItem(MIGRATION_CHECK_KEY);
    localStorage.removeItem(MIGRATION_VERSION_KEY);
    hasChecked.current = false;
    setState({
      isRunning: false,
      progress: 0,
      total: 0,
      completed: 0,
      errors: [],
      hasRun: false,
      lastCheck: null
    });
  }, []);

  return {
    ...state,
    checkMigrationNeeded,
    runAutoMigration: manualMigration,
    triggerMigrationIfNeeded,
    resetMigrationState
  };
};